<template>
  <v-app>
    <v-app-bar app color="primary" dark elevation="4" class="px-2">
      <div class="d-flex align-center">
        <v-img
          alt="档案馆印章"
          class="shrink mr-2 d-none d-sm-flex"
          contain
          src="@/assets/seal.svg"
          transition="scale-transition"
          width="40"
        />

        <router-link to="/" class="text-decoration-none">
          <h1 class="text-h5 text-sm-h4 font-weight-bold site-title" style="color: #FFFFFF !important; text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7); letter-spacing: 1px;">清算中共.档案馆</h1>
        </router-link>
      </div>

      <v-spacer></v-spacer>

      <!-- Desktop Navigation -->
      <div class="d-none d-md-block">
        <v-btn
          v-for="item in menuItems"
          :key="item.title"
          :to="item.to"
          variant="text"
          class="mx-1"
          :exact="item.exact"
        >
          <v-icon start>{{ item.icon }}</v-icon>
          {{ $t(item.title) }}
        </v-btn>

        <!-- 管理菜单，仅对已登录用户显示 -->
        <template v-if="isLoggedIn">
          <v-btn
            v-for="item in adminMenuItems"
            :key="item.title"
            :to="item.to"
            variant="text"
            class="mx-1"
            :exact="item.exact"
          >
            <v-icon start>{{ item.icon }}</v-icon>
            {{ $t(item.title) }}
          </v-btn>
        </template>

        <!-- 登录/登出按钮 -->
        <v-btn
          v-if="isLoggedIn"
          variant="text"
          class="mx-1"
          @click="logout"
        >
          <v-icon start>mdi-logout</v-icon>
          {{ $t('auth.logout') }}
        </v-btn>
        <v-btn
          v-else
          variant="text"
          class="mx-1"
          to="/login"
        >
          <v-icon start>mdi-login</v-icon>
          {{ $t('auth.login') }}
        </v-btn>
      </div>

      <!-- Mobile Navigation -->
      <v-app-bar-nav-icon
        class="d-md-none"
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <!-- 添加语言切换按钮 -->
      <v-btn
        icon
        @click="toggleLanguage"
        class="mr-2"
      >
        <v-icon>{{ currentLanguage === 'zh' ? 'mdi-translate' : 'mdi-translate' }}</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      temporary
      location="right"
    >
      <v-list>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6">{{ $t('home.title') }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t('home.subtitle') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :to="item.to"
          :exact="item.exact"
          color="primary"
        >
          <template v-slot:prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
        </v-list-item>

        <!-- 管理菜单，仅对已登录用户显示 -->
        <template v-if="isLoggedIn">
          <v-list-item
            v-for="item in adminMenuItems"
            :key="item.title"
            :to="item.to"
            :exact="item.exact"
            color="primary"
          >
            <template v-slot:prepend>
              <v-icon>{{ item.icon }}</v-icon>
            </template>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item>
        </template>

        <v-divider></v-divider>

        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-subtitle-1">{{ $t('home.aboutUs.title') }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption">{{ $t('home.aboutUs.content') }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <!-- 登录/登出按钮 -->
      <v-list>
        <v-list-item
          v-if="isLoggedIn"
          @click="logout"
          color="primary"
        >
          <template v-slot:prepend>
            <v-icon>mdi-logout</v-icon>
          </template>
          <v-list-item-title>{{ $t('auth.logout') }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-else
          to="/login"
          color="primary"
        >
          <template v-slot:prepend>
            <v-icon>mdi-login</v-icon>
          </template>
          <v-list-item-title>{{ $t('auth.login') }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-4">
          <v-btn block color="secondary" @click="drawer = false">
            {{ $t('home.footer.closeMenu') }}
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main class="main-content">
      <v-container fluid class="archive-container pa-6">
        <router-view v-slot="{ Component }">
          <transition name="page" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </v-container>
    </v-main>

    <!-- Global Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      :location="snackbar.location"
    >
      {{ snackbar.text }}

      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          关闭
        </v-btn>
      </template>
    </v-snackbar>

    <v-footer color="secondary" dark class="py-3 mt-auto">
      <v-container>
        <v-row align="start">
          <v-col cols="12" md="4" class="mb-2 mb-md-0">
            <div class="d-flex align-center mb-2">
              <v-img
                alt="档案馆印章"
                class="mr-2"
                contain
                src="@/assets/seal.svg"
                width="32"
              />
              <h3 class="text-h6 font-weight-bold footer-title mb-0" style="color: #FFD700 !important; text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8); letter-spacing: 1px;">{{ $t('home.title') }}</h3>
            </div>
            <p class="text-body-2 footer-text mt-2">
              {{ $t('home.description') }}
            </p>
          </v-col>

          <v-col cols="12" md="4" class="mb-2 mb-md-0">
            <h4 class="text-h6 mb-2 footer-heading" style="color: #FFD700 !important; text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7);">{{ $t('home.quickLinks.title') }}</h4>
            <v-list density="compact" bg-color="transparent" class="pa-0">
              <v-list-item
                v-for="item in menuItems"
                :key="item.title"
                :to="item.to"
                :exact="item.exact"
                density="compact"
                class="px-0 py-1"
                color="accent"
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="accent">{{ item.icon }}</v-icon>
                </template>
                <v-list-item-title class="footer-menu-item">{{ $t(item.title) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>

          <v-col cols="12" md="4">
            <h4 class="text-h6 mb-2 footer-heading">{{ $t('home.aboutUs.title') }}</h4>
            <p class="text-body-2 footer-text">
              {{ $t('home.aboutUs.content') }}
            </p>
          </v-col>
        </v-row>

        <v-divider class="my-2" color="accent"></v-divider>

        <div class="text-center text-caption footer-copyright">
          {{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}
        </div>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>
import AuthService from '@/services/AuthService';
import { useI18n } from 'vue-i18n'

export default {
  name: 'App',

  data() {
    return {
      drawer: false,
      menuItems: [
        { title: 'home.quickLinks.home', icon: 'mdi-home', to: '/', exact: true },
        { title: 'home.quickLinks.confidential', icon: 'mdi-file-document', to: '/confidential', exact: false },
        { title: 'home.quickLinks.verdicts', icon: 'mdi-gavel', to: '/verdicts', exact: false },
        { title: 'home.quickLinks.stories', icon: 'mdi-book-open-variant', to: '/stories', exact: false }
      ],
      adminMenuItems: [
        { title: 'home.quickLinks.admin', icon: 'mdi-cog', to: '/admin', exact: false }
      ],
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 3000,
        location: 'top'
      },
      loginState: false
    }
  },
  computed: {
    isLoggedIn() {
      return this.loginState || AuthService.isLoggedIn();
    },
    currentUser() {
      return AuthService.getUser();
    }
  },
  created() {
    this.updateLoginState();
    window.addEventListener('storage', this.updateLoginState);
    window.addEventListener('login-success', this.updateLoginState);
  },
  watch: {
    $route() {
      this.updateLoginState();
    }
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.updateLoginState);
    window.removeEventListener('login-success', this.updateLoginState);
  },
  methods: {
    updateLoginState() {
      this.loginState = AuthService.isLoggedIn();
      console.log('Login state updated:', this.loginState);
    },
    showSnackbar(payload) {
      this.snackbar.text = payload.text || 'Operation completed';
      this.snackbar.color = payload.color || 'success';
      this.snackbar.timeout = payload.timeout || 3000;
      this.snackbar.location = payload.location || 'top';
      this.snackbar.show = true;
    },
    logout() {
      AuthService.logout();
      this.updateLoginState();
      this.showSnackbar({
        text: '已成功登出',
        color: 'success'
      });
      if (this.$route.path.startsWith('/admin')) {
        this.$router.push('/');
      }
    }
  },
  provide() {
    return {
      showSnackbar: this.showSnackbar
    };
  },
  setup() {
    const { locale } = useI18n()
    
    const toggleLanguage = () => {
      const newLocale = locale.value === 'zh' ? 'en' : 'zh'
      locale.value = newLocale
      localStorage.setItem('language', newLocale)
    }

    return {
      currentLanguage: locale,
      toggleLanguage
    }
  }
}
</script>

<style>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&family=Noto+Serif+SC:wght@400;600;700&family=Playfair+Display:wght@400;700&display=swap');

/* Global styles are in assets/styles.css */

/* App layout styles */
.v-application {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex: 1 0 auto;
  min-height: calc(100vh - 64px - 220px); /* 减去header和footer的大致高度，减小footer高度 */
}

.v-footer {
  flex-shrink: 0;
}

/* 网站标题样式 */
.v-app-bar h1.site-title,
.site-title {
  color: #FFFFFF !important; /* 纯白色 */
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7) !important; /* 增强文字阴影 */
  letter-spacing: 1px !important; /* 增加字间距 */
  font-weight: 700 !important; /* 加粗 */
}

/* 页脚标题样式 */
.v-footer .footer-title,
.footer-title {
  color: #FFD700 !important; /* 金色 */
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8) !important;
  letter-spacing: 1px !important;
  font-weight: 700 !important;
}

/* 页脚标题样式 */
.v-footer .footer-heading,
.footer-heading {
  color: #FFD700 !important; /* 金色 */
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7) !important;
  font-weight: bold !important;
  letter-spacing: 0.5px !important;
}

/* 页脚文本样式 */
.footer-text {
  color: #E0E0E0 !important; /* 浅灰白色 */
  line-height: 1.4; /* 减小行高 */
  margin-bottom: 0; /* 移除底部边距 */
}

/* 页脚版权信息样式 */
.footer-copyright {
  color: #CCCCCC !important; /* 浅灰色 */
  margin-top: 4px; /* 减小顶部边距 */
  margin-bottom: 4px; /* 减小底部边距 */
}

/* 页脚菜单项样式 */
.footer-menu-item {
  color: #E0E0E0 !important; /* 浅灰白色 */
}

/* 页脚列表项样式 */
.v-footer .v-list-item {
  min-height: 32px !important; /* 减小列表项高度 */
}

/* 页脚容器样式 */
.v-footer .v-container {
  padding-top: 8px;
  padding-bottom: 8px;
}
</style>
